import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Zap, Copy, Check, Percent, CreditCard } from 'lucide-react';
import { toast } from 'sonner';

interface Invoice {
  id: string;
  amount: number;
  due_date: string;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
}

interface DealSimulatorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoices: Invoice[];
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value / 100);
}

function getDaysOverdue(dueDate: string): number {
  const due = new Date(dueDate);
  const today = new Date();
  const diffTime = today.getTime() - due.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
}

export function DealSimulatorModal({ open, onOpenChange, invoices }: DealSimulatorModalProps) {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isCashDiscount, setIsCashDiscount] = useState(true);
  const [discount, setDiscount] = useState(10);
  const [installments, setInstallments] = useState(3);
  const [copied, setCopied] = useState(false);

  // Filter only pending/overdue invoices
  const eligibleInvoices = invoices.filter(inv => 
    inv.status === 'pending' || inv.status === 'overdue'
  );

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const selectAll = () => {
    if (selectedIds.length === eligibleInvoices.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(eligibleInvoices.map(inv => inv.id));
    }
  };

  const calculation = useMemo(() => {
    const selectedInvoices = eligibleInvoices.filter(inv => selectedIds.includes(inv.id));
    const valorOriginal = selectedInvoices.reduce((sum, inv) => sum + inv.amount, 0);
    const descontoValor = isCashDiscount ? (valorOriginal * (discount / 100)) : 0;
    const novoTotal = valorOriginal - descontoValor;
    const valorParcela = !isCashDiscount ? (novoTotal / installments) : novoTotal;

    return {
      valorOriginal,
      descontoValor,
      novoTotal,
      valorParcela,
      numParcelas: isCashDiscount ? 1 : installments
    };
  }, [selectedIds, eligibleInvoices, isCashDiscount, discount, installments]);

  const handleGenerateLink = () => {
    // Mock link generation
    const mockLink = `https://pay.digisend.com.br/acordo/${Date.now().toString(36)}`;
    navigator.clipboard.writeText(mockLink);
    setCopied(true);
    toast.success('Link de acordo copiado!', {
      description: mockLink
    });
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-[hsl(var(--careful-500))]" />
            Simular Acordo
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Invoice Selection */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Selecione as Faturas
              </Label>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={selectAll}
                className="text-xs"
              >
                {selectedIds.length === eligibleInvoices.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
              </Button>
            </div>

            {eligibleInvoices.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground bg-muted/30 rounded-lg">
                Não há faturas pendentes para negociação.
              </div>
            ) : (
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                {eligibleInvoices.map((invoice) => {
                  const daysOverdue = getDaysOverdue(invoice.due_date);
                  const isOverdue = daysOverdue > 0;
                  
                  return (
                    <label
                      key={invoice.id}
                      className="flex items-center gap-4 p-3 hover:bg-muted/30 cursor-pointer transition-colors"
                    >
                      <Checkbox
                        checked={selectedIds.includes(invoice.id)}
                        onCheckedChange={() => toggleSelection(invoice.id)}
                      />
                      <div className="flex-1">
                        <span className="text-sm text-muted-foreground">
                          Venc. {new Date(invoice.due_date).toLocaleDateString('pt-BR')}
                        </span>
                        {isOverdue && (
                          <span className="ml-2 text-xs text-[hsl(var(--careful-600))] font-medium">
                            ({daysOverdue} dias em atraso)
                          </span>
                        )}
                      </div>
                      <span className="font-mono font-medium">
                        {formatCurrency(invoice.amount)}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Parameters */}
          <div className="grid grid-cols-2 gap-4">
            {/* Mode Toggle */}
            <div className="col-span-2">
              <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 block">
                Tipo de Acordo
              </Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={isCashDiscount ? 'default' : 'outline'}
                  onClick={() => setIsCashDiscount(true)}
                  className={`flex-1 gap-2 ${isCashDiscount ? 'bg-[hsl(var(--accent-500))] hover:bg-[hsl(var(--accent-600))]' : ''}`}
                >
                  <Percent className="h-4 w-4" />
                  Desconto à Vista
                </Button>
                <Button
                  type="button"
                  variant={!isCashDiscount ? 'default' : 'outline'}
                  onClick={() => setIsCashDiscount(false)}
                  className={`flex-1 gap-2 ${!isCashDiscount ? 'bg-[hsl(var(--accent-500))] hover:bg-[hsl(var(--accent-600))]' : ''}`}
                >
                  <CreditCard className="h-4 w-4" />
                  Parcelamento
                </Button>
              </div>
            </div>

            {/* Discount Slider */}
            {isCashDiscount && (
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm">Desconto</Label>
                  <span className="text-sm font-bold text-[hsl(var(--accent-600))]">{discount}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="50"
                  value={discount}
                  onChange={(e) => setDiscount(parseInt(e.target.value))}
                  className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-[hsl(var(--accent-500))]"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>0%</span>
                  <span>50%</span>
                </div>
              </div>
            )}

            {/* Installments */}
            {!isCashDiscount && (
              <div className="col-span-2">
                <Label className="text-sm mb-2 block">Número de Parcelas</Label>
                <div className="flex gap-2">
                  {[2, 3, 4, 6, 8, 10, 12].map((num) => (
                    <Button
                      key={num}
                      type="button"
                      variant={installments === num ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setInstallments(num)}
                      className={installments === num ? 'bg-[hsl(var(--accent-500))] hover:bg-[hsl(var(--accent-600))]' : ''}
                    >
                      {num}x
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Result Card */}
          <div className="bg-gradient-to-br from-[hsl(var(--accent-50))] to-[hsl(var(--basic-50))] rounded-xl p-6 border border-[hsl(var(--accent-200))]">
            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-4">
              Resultado do Acordo
            </h3>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Valor Original</span>
                <span className="font-mono text-lg">
                  {formatCurrency(calculation.valorOriginal)}
                </span>
              </div>

              {isCashDiscount && calculation.descontoValor > 0 && (
                <div className="flex justify-between items-center text-[hsl(var(--accent-600))]">
                  <span>Desconto ({discount}%)</span>
                  <span className="font-mono text-lg">
                    - {formatCurrency(calculation.descontoValor)}
                  </span>
                </div>
              )}

              <div className="border-t pt-3">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-foreground">
                    {isCashDiscount ? 'Novo Total' : `${calculation.numParcelas}x de`}
                  </span>
                  <span className="font-mono text-2xl font-bold text-[hsl(var(--accent-700))]">
                    {formatCurrency(calculation.valorParcela)}
                  </span>
                </div>
              </div>
            </div>

            {selectedIds.length === 0 && (
              <p className="text-xs text-muted-foreground mt-4 text-center italic">
                Selecione ao menos uma fatura para simular
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancelar
            </Button>
            <Button 
              onClick={handleGenerateLink}
              disabled={selectedIds.length === 0}
              className="flex-1 gap-2 bg-[hsl(var(--accent-500))] hover:bg-[hsl(var(--accent-600))]"
            >
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? 'Copiado!' : 'Gerar Link de Acordo'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
